import { useEffect } from 'react';
import { isEmpty } from 'lodash';

import API from '../../api';
import { COOKIE_KEYS, LOCAL_STORAGE_TYPES } from '../../constants';
import {
  UserContainer,
  ClientContainer,
  ApplicationContainer,
} from '../../providers';
import useLocalStorage from '../localstorage';
import { useGetClients } from './client';
import { useGetPages } from './pages';
import { useNetwork } from '../use-network';

const useGetBusiness = () => {
  const {
    state: userState,
    setBusiness,
    setBusinessOptions,
    setPaymentPlans,
  } = UserContainer.useContainer();

  const {
    state: clientState,
    updateSelectedClient,
  } = ClientContainer.useContainer();
  const getPages = useGetPages();
  const getClients = useGetClients();
  const { set, get } = useLocalStorage();

  useEffect(() => {
    const { clients } = clientState ?? {};
    if (clientState?.clients?.length) {
      const workspaceDetail = get(LOCAL_STORAGE_TYPES.WORKSPACE);
      const clientId =
        clients.find(
          c => c.id.toString() === workspaceDetail?.client?.toString()
        )?.id ?? clients[0].id;

      // set(LOCAL_STORAGE_TYPES.WORKSPACE, {
      //   client: clientId,
      // });
      updateSelectedClient(clientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientState?.clients]);

  const getBusiness = async (query, invited = false) => {
    const isAccount = /accounts/gi.test(query?.fields);

    const businessQuery = {
      ...query,
    };

    if (isAccount) {
      businessQuery.fields = businessQuery?.fields.replace('accounts', '');
    }
    businessQuery.fields = `${businessQuery.fields}${
      businessQuery.fields ? ',' : ''
    }billing`;
    const response = await API.business.find(businessQuery);

    // Plans do not come in response.data
    const plans = response?.paymentPlans;
    if (plans && Object.keys(plans).length) {
      setPaymentPlans(plans);
    }

    if (!response.data[0]) return false;
    const businessOptions = response.data;

    let business;
    const workspaceDetail = get(LOCAL_STORAGE_TYPES.WORKSPACE);

    if (workspaceDetail?.businessId) {
      business = businessOptions.find(
        ({ id }) => workspaceDetail?.businessId?.toString() === id?.toString()
      );
    }

    if (business) {
      if (
        business.access.invitationDate !== null &&
        business.access.acceptedInviteDate === null
      ) {
        business = businessOptions.find(
          ({ access }) =>
            access.acceptedInviteDate === null && access.invitationDate === null
        );
      }
    }

    const checkInvitationInLocalStorage = get(
      LOCAL_STORAGE_TYPES.INVITATION_TOKEN
    );
    if (
      invited ||
      userState?.user?.invitationToken ||
      !isEmpty(checkInvitationInLocalStorage)
    ) {
      business = businessOptions.find(({ access }) => access.isPrimary);
    }

    // Fallback: prioritize primary business over first business in array
    // This ensures new users land on their primary business, not demo business
    business =
      business ??
      businessOptions.find(({ access }) => access.isPrimary) ??
      businessOptions[0];

    // update iff business exists
    if (business) {
      const businessId = business.id;
      set(LOCAL_STORAGE_TYPES.WORKSPACE, {
        businessId,
      });
      const { clients } = business;

      if (clients) {
        getClients(businessId);
      }

      getPages(businessId);
      setBusiness(business);
      setBusinessOptions(businessOptions);
    }

    return true;
  };

  return getBusiness;
};

const usePatchBusiness = () => {
  const { state: userState, setBusiness } = UserContainer.useContainer();
  const patchBusiness = async payload => {
    const response = await API.business.update(userState.business.id, payload);
    setBusiness({ ...userState.business, ...response });
  };
  return patchBusiness;
};

export const usePatchTeamBusiness = () => {
  const { state: userState, setBusiness } = UserContainer.useContainer();
  const { patch } = useNetwork();
  const patchBusiness = async () => {
    const teamBusinessId = userState.business?.id;
    const userBusinessId = userState.businessOptions?.find?.(
      business => business.access.isPrimary === true
    )?.id;

    if (teamBusinessId && userBusinessId) {
      const response = await patch(
        `/update-activation/${teamBusinessId}?businessId=${userBusinessId}`
      );
      setBusiness({ ...userState.business, ...response });
    }
  };
  return patchBusiness;
};

const useCreateBusiness = () => {
  const { setBusiness } = UserContainer.useContainer();
  const createBusiness = async payload => {
    const business = await API.business.create(payload);
    setBusiness(business);
    return business;
  };
  return createBusiness;
};

const usePatchAccess = () => {
  const { state: userState, setBusiness } = UserContainer.useContainer();
  const patchAccess = async payload => {
    const accessId = userState.business?.access?.id;
    const businessId = userState.business?.id;
    const response = await API.access.update(accessId, {
      businessId,
      ...payload,
    });

    setBusiness({ ...userState.business, access: response });
  };

  return patchAccess;
};

const useChangeBusiness = () => {
  const { setBusiness } = UserContainer.useContainer();
  const getPages = useGetPages();
  const getClients = useGetClients();
  const { setApplicationLoaded } = ApplicationContainer.useContainer();
  const { set, get } = useLocalStorage();

  const changeBusiness = async business => {
    setApplicationLoaded(false);
    const accessQuery = { businessId: business.id };
    const [accessResponse, billingResponse] = await Promise.all([
      API.access.get(accessQuery),
      API.billing.getInfo(business.id),
    ]);
    const access = accessResponse.data[0];
    const billing = billingResponse;
    const { clients } = business;

    // Await getClients to ensure clients are fetched and populated before continuing
    // This is critical for business switching to work correctly
    if (clients) {
      await getClients(business.id);
    }

    getPages(business.id);
    setBusiness({ ...business, access, billing });

    const workspaceDetail = get(LOCAL_STORAGE_TYPES.WORKSPACE);

    const { id: businessId } = business;
    const firstClientId = clients?.[0]?.id;
    const workspaceData = {
      ...(workspaceDetail ?? {}),
      businessId,
      // Never persist `client: undefined` — the merge-set would drop the key
      // and leave a client from the PREVIOUS business behind. When the new
      // business has no client yet, clear the slot explicitly.
      client: firstClientId ?? null,
    };
    setApplicationLoaded(true);
    set(LOCAL_STORAGE_TYPES.WORKSPACE, workspaceData);
  };

  return changeBusiness;
};

const useChangeBusinessPrimary = () => {
  const { state: userState, setBusinessOptions } = UserContainer.useContainer();
  const changeBusinessPrimary = async (id, businessId, isPrimary) => {
    const response = await API.access.update(
      id,
      {
        isPrimary,
      },
      { businessId }
    );

    const updatedBusiness = userState.businessOptions.map(business => {
      if (business.id === response.businessId) {
        return { ...business, access: response };
      }

      return business;
    });

    setBusinessOptions(updatedBusiness);
  };

  return changeBusinessPrimary;
};

export {
  useGetBusiness,
  usePatchBusiness,
  useCreateBusiness,
  usePatchAccess,
  useChangeBusiness,
  useChangeBusinessPrimary,
};
